import { isFunction } from "lodash-es";
import {
  MediaCaptionsButton,
  MediaControlBar,
  MediaController,
  MediaFullscreenButton,
  MediaLoadingIndicator,
  MediaMuteButton,
  MediaPlayButton,
  MediaPlaybackRateButton,
  MediaSeekBackwardButton,
  MediaSeekForwardButton,
  MediaTimeDisplay,
  MediaTimeRange,
  MediaVolumeRange,
} from "media-chrome/react";
import { MediaProvider } from "media-chrome/react/media-store";
import { useMemo } from "react";

type RecordingPlayerProps = {
  recording: {
    id: string;
    videoURL: string;
    duration: string;
    name: string;
    imageURL: string;
  };
  transcript?: string | null;
  videoRef: React.RefObject<HTMLVideoElement>;
  setIsPlaying?: (isPlaying: boolean) => void;
  setCurrentTime?: (currentTime: number) => void;
};

export function RecordingPlayer({
  recording,
  transcript,
  videoRef,
  setIsPlaying,
  setCurrentTime,
}: RecordingPlayerProps) {
  const vttBlobUrl = useMemo(() => {
    if (!transcript) return undefined;
    const blob = new Blob([transcript], { type: "text/vtt" });
    return URL.createObjectURL(blob);
  }, [transcript]);

  return (
    <div className="relative aspect-video w-full cursor-pointer overflow-hidden rounded-lg bg-black">
      <MediaProvider>
        <MediaController
          style={{
            width: "100%",
            aspectRatio: "16/9",
          }}
        >
          <video
            slot="media"
            playsInline
            ref={videoRef}
            preload="auto"
            poster={recording.imageURL}
            onContextMenu={(e) => e.preventDefault()}
            onTimeUpdate={(e) => {
              const time = (e.target as HTMLMediaElement).currentTime;
              if (isFunction(setCurrentTime)) {
                setCurrentTime(time);
              }
            }}
            onPlay={() => {
              if (isFunction(setIsPlaying)) {
                setIsPlaying(true);
              }
            }}
            onPlaying={() => {
              if (isFunction(setIsPlaying)) {
                setIsPlaying(true);
              }
            }}
            onPause={() => {
              if (isFunction(setIsPlaying)) {
                setIsPlaying(false);
              }
            }}
            onEnded={() => {
              if (isFunction(setIsPlaying)) {
                setIsPlaying(false);
              }
            }}
          >
            <source src={recording.videoURL} type="video/mp4" />
            {vttBlobUrl && (
              <track
                label="English"
                kind="captions"
                srcLang="en"
                src={vttBlobUrl}
                className="mb-12"
              />
            )}
          </video>

          <MediaLoadingIndicator
            slot="centered-chrome"
            className="bg-transparent"
          />
          <MediaControlBar
            className="top-auto m-2 rounded-md backdrop-blur-md"
            style={{
              background:
                "radial-gradient(85.77% 49.97% at 51% 5.12%, rgba(255, 255, 255, .15) 0, rgba(222, 226, 255, .08) 45.83%, rgba(241, 242, 255, .1) 100%), rgba(0, 0, 0, .2)",
              boxShadow:
                "inset 0 1px 0 0 hsla(0,0%,100%,.2), 0px 2px 4px 0px rgba(0, 0, 0, 0.1), 0px 6px 15px 0px rgba(0, 0, 0, 0.15)",
            }}
          >
            <MediaPlayButton className="bg-transparent" />
            <MediaSeekBackwardButton
              seekOffset={10}
              className="hidden bg-transparent sm:block"
            />
            <MediaSeekForwardButton
              seekOffset={10}
              className="hidden bg-transparent sm:block"
            />
            <MediaTimeRange className="bg-transparent" />
            <MediaTimeDisplay
              showDuration
              className="hidden bg-transparent sm:block"
            />
            <MediaMuteButton className="hidden bg-transparent sm:block" />
            <MediaVolumeRange className="hidden bg-transparent sm:block" />
            <MediaPlaybackRateButton className="bg-transparent" />
            {vttBlobUrl && <MediaCaptionsButton className="bg-transparent" />}
            <MediaFullscreenButton className="bg-transparent" />
          </MediaControlBar>
        </MediaController>
      </MediaProvider>
    </div>
  );
}
